import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import {
  ArrowLeft,
  CalendarClock,
  Clock,
  ExternalLink,
  Loader2,
  MapPin,
  Pencil,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { useToast } from '@/shared/hooks/use-toast';
import { useMeeting, useMeetingMutations } from '../hooks';
import type { MeetingStatus, UserMinimal } from '../types';
import {
  MEETING_STATUS_LABELS,
  MEETING_TYPE_LABELS,
} from '../types';
import { MeetingFormDialog } from './MeetingFormDialog';
import { MEETINGS_DETAIL_TOUR_ID } from '@/features/tours/meetings/detail';
import { MEETINGS_COMBINED_TOUR_ID } from '@/features/tours/meetings/combined';
import { useTour } from '@/features/tours';

const STATUS_COLORS: Record<MeetingStatus, string> = {
  SCHEDULED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-slate-100 text-slate-700',
};

const formatWhen = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (mins: number): string => {
  if (!mins || mins <= 0) return '—';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

const fullName = (user: UserMinimal | null): string => {
  if (!user) return '—';
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return name || user.email;
};

const splitExternal = (raw: string): string[] =>
  raw
    .replace(/,/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const TextSection: React.FC<{
  label: string;
  value: string;
  emptyHint?: string;
}> = ({ label, value, emptyHint }) => (
  <div className="space-y-1.5">
    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
      {label}
    </h3>
    {value.trim() ? (
      <p className="text-sm whitespace-pre-wrap leading-relaxed">{value}</p>
    ) : (
      <p className="text-sm italic text-muted-foreground">
        {emptyHint || 'Not provided.'}
      </p>
    )}
  </div>
);

export const MeetingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const backPath = location.pathname.replace(/\/[^/]+$/, '');

  const { data: meeting, isLoading } = useMeeting(id);
  const { deleteItem } = useMeetingMutations(numericProjectId);

  const [isEditOpen, setIsEditOpen] = useState(false);

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!meeting) return;
    if (hasSeen(MEETINGS_COMBINED_TOUR_ID)) return;
    if (hasSeen(MEETINGS_DETAIL_TOUR_ID)) return;
    void startTour(MEETINGS_DETAIL_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, meeting]);

  const handleDelete = async () => {
    if (!id) return;
    const confirmed = window.confirm(
      'Delete this meeting? This action cannot be undone.',
    );
    if (!confirmed) return;
    try {
      await deleteItem.mutateAsync(id);
      toast({ title: 'Meeting deleted' });
      navigate(backPath);
    } catch (error) {
      toast({
        title: 'Failed to delete meeting',
        description:
          error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading || !meeting) {
    return (
      <BaseLayout>
        <div className="p-6 max-w-5xl mx-auto flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading meeting…
        </div>
      </BaseLayout>
    );
  }

  const externalAttendees = splitExternal(meeting.attendees_external);

  return (
    <BaseLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backPath)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold truncate">
                {meeting.title}
              </h1>
              <p className="text-xs text-muted-foreground">
                {MEETING_TYPE_LABELS[meeting.meeting_type] ||
                  meeting.meeting_type_display}
                {' · Created '}
                {new Date(meeting.created_at).toLocaleString()}
                {meeting.created_by ? ` by ${fullName(meeting.created_by)}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-tour-id="meeting-detail-header-actions">
            <Badge className={STATUS_COLORS[meeting.status]}>
              {MEETING_STATUS_LABELS[meeting.status]}
            </Badge>
            <Button
              variant="outline"
              onClick={() => setIsEditOpen(true)}
              className="gap-1.5"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleteItem.isPending}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        <Card data-tour-id="meeting-detail-metadata">
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">When</p>
                  <p className="font-medium">{formatWhen(meeting.scheduled_at)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-medium">
                    {formatDuration(meeting.duration_minutes)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-medium">{meeting.location || '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Organizer</p>
                  <p className="font-medium">{fullName(meeting.organizer)}</p>
                </div>
              </div>
            </div>
            {meeting.meeting_link && (
              <div className="mt-3">
                <a
                  href={meeting.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Join meeting
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-tour-id="meeting-detail-attendees">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Attendees
              <span className="ml-1 text-xs text-muted-foreground font-normal">
                ({meeting.attendee_count})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Internal
              </h4>
              {meeting.attendees_internal.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">
                  No internal attendees.
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {meeting.attendees_internal.map((u) => (
                    <li key={u.id} className="flex items-center gap-2">
                      <span className="font-medium">{fullName(u)}</span>
                      <span className="text-xs text-muted-foreground">
                        {u.email}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                External
              </h4>
              {externalAttendees.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">
                  No external attendees.
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {externalAttendees.map((line, idx) => (
                    <li key={`${line}-${idx}`}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-tour-id="meeting-detail-content">
          <CardContent className="py-4 space-y-5">
            <TextSection
              label="Agenda"
              value={meeting.agenda}
              emptyHint="No agenda was provided."
            />
            <TextSection
              label="Summary / Minutes"
              value={meeting.summary}
              emptyHint="No summary captured yet."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TextSection
                label="Key Decisions"
                value={meeting.decisions}
                emptyHint="No decisions recorded."
              />
              <TextSection
                label="Action Items"
                value={meeting.action_items}
                emptyHint="No action items recorded."
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <MeetingFormDialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        projectId={numericProjectId}
        meeting={meeting}
      />
    </BaseLayout>
  );
};
